import { Injectable, OnDestroy } from '@angular/core';
import { firstValueFrom, Subscription } from 'rxjs';
import { CouponFeedItem } from 'src/app/shared/models/coupon-feed-item/coupon-feed-item';
import { Region } from 'src/app/shared/models/dtos/region/region';
import { RewardService } from '../../api/reward/reward.service';
import { UserService } from '../../api/user/user.service';
import { SnackBarService } from '../snackbar/snackbar.service';
import { TranslationService } from '../translation/translation.service';

export type ActivitiesFeedList = CouponFeedItem[];

@Injectable({
  providedIn: 'root',
})
/**
 * Service to interact with LnR's Reward Service
 */
export class FeedService implements OnDestroy {
  availableRegions?: Region[];
  filters = {
    startDate: '',
    endDate: '',
    coupon: false,
    vip: false,
    series: [''],
    status: [''],
    countries: [''],
    search: '',
  };
  private subsink = new Subscription();
  test = undefined;

  /**
   * @constructor
   */
  constructor(
    private rewardService: RewardService,
    private userService: UserService,
    private snackBarService: SnackBarService,
    private translationService: TranslationService
  ) {
    this.subsink.add(
      this.userService.getRegions().subscribe((regions) => {
        this.availableRegions = regions;
      })
    );
  }

  /**
   * Clean up resources
   */
  ngOnDestroy() {
    this.subsink.unsubscribe();
  }

  /**
   * Fetches coupons from reward service
   * @param {number} pageNumber
   * @param {number} pageSize
   */
  async fetchCoupons(pageNumber: number, pageSize: number) {
    try {
      const feedFilters: any = {
        page: pageNumber,
        pageSize: pageSize,
        status: this.filters.status,
        vip: this.filters.vip,
        series: this.filters.series,
        countries: this.filters.countries,
        search: this.filters.search,
      };

      if (this.filters.startDate) {
        feedFilters.startDate = this.filters.startDate;
        feedFilters.endDate = this.filters.endDate;
      }

      const coupons$ = this.rewardService.getCouponsFeed(feedFilters);
      return await firstValueFrom(coupons$);
    } catch {
      this.translationService
        .translateString('coupons-error-feed')
        .then((str) => this.snackBarService.error(str));

      return [];
    }
  }

  /**
   * Sorts a feed list
   * @param {ActivitiesFeedList} feed
   * @return {ActivitiesFeedList}
   */
  static sortFeed(feed: ActivitiesFeedList): ActivitiesFeedList {
    feed.sort((a, b) => {
      const x = a.displayDate;
      const y = b.displayDate;

      return (x < y && 1) || (x > y && -1) || 0;
    });

    // It sorts cases that the displayDate are equal
    // We use expireDate as comparison criteria, in which the item with most recent expireDate appears first
    feed.sort((a, b) => {
      const x = a.expireDate;
      const y = b.expireDate;

      const z = new Date(a.displayDate);
      const w = new Date(b.displayDate);

      if (z.getTime() === w.getTime()) {
        return (x > y && 1) || (x < y && -1) || 0;
      } else return 0;
    });

    return feed;
  }
}
